import { StoreKit, makeSelectFeatureFlag } from '@nx/state';
import { createAsyncThunk } from '@reduxjs/toolkit';

import { log } from '../../services/logging';
import { getAllScenes } from '../../services/scene';
import { isVersion5OorGreater } from '../../services/versions/versionUtils';
import type { Perspective } from '../../types/perspective';
import type { Scene } from '../../types/scene';
import type { Nullable } from '../../types/utility';
import {
  getServerVersion,
  getUserId,
  getUserRoles,
  isPerspectiveSharingAvailable,
  selectUserCanSaveScene,
} from '../connections/connectionsDuck';
import { getVisibleLabels, getVisibleRelationshipTypes } from '../perspectives/perspectives';
import { getPerspective } from '../perspectives/perspectives.selector';
import { getEnableCytoscape } from '../settings/settings';
import { getStyleById } from '../styles/styles';
import type { RootState } from '../types';
import { getLayoutType } from '../visualization/visualization';
import { setScenes, setStartWithProvidedGraph } from './scene';
import {
  getCurrentSceneId,
  getScenes,
  getStartDeepLinkedScene,
  getStartWithProvidedGraph,
  isStartWithNewSceneActive,
} from './scene.selectors';
import type { SceneState } from './scene.types';
import { REFRESH_SCENES_ACTION } from './scene.types';
import { applyNewEmptyScene as applyNewEmptySceneGenerator, applySelectedScene } from './sceneActions';

export const refreshScenes = createAsyncThunk<void, void, { state: RootState }>(
  REFRESH_SCENES_ACTION,
  async (_, { dispatch, getState }) => {
    const state = getState();
    const userId = getUserId(state);
    const userRoles = getUserRoles(state) ?? [];
    const perspective = getPerspective(state);
    const startWithNewScene = isStartWithNewSceneActive(state);
    const currentSceneId = getCurrentSceneId(state);
    const deepLinkedSceneId = getStartDeepLinkedScene(state);
    let providedGraph = getStartWithProvidedGraph(state);
    const scenes = getScenes(state);
    const perspectiveStyle = getStyleById(state)(perspective?.id);
    const canSaveScene = selectUserCanSaveScene(state);
    const hasPlugin = isPerspectiveSharingAvailable(state);

    const { globalStore } = StoreKit.UNSAFE_getGlobalStoreInstance();
    const showViewInExplore = makeSelectFeatureFlag('explore:query-view-in-explore')(globalStore.getState());
    const isProvidedGraphAvailable = providedGraph && showViewInExplore;

    if (userId == null || !perspective || (!hasPlugin && !isProvidedGraphAvailable)) {
      return;
    }

    const applyNewEmptyScene = applyNewEmptySceneGenerator({ perspectiveStyle, canSaveScene, userId, dispatch });
    const updatedScenes = (await fetchAllScenes(userId ?? '', perspective, userRoles, scenes)) ?? [];
    dispatch(setScenes(updatedScenes));
    const orderedMyScenes = updatedScenes
      .filter((s) => s.createdBy === userId)
      .sort((s1, s2) => s2.createdAt.getTime() - s1.createdAt.getTime());

    const lastVisitedScene = updatedScenes.find(({ id }) => id === currentSceneId);
    const temporaryScene = scenes.find(
      (scene) => scene.temporaryData?.perspectiveId === perspective?.id && scene.createdBy === userId,
    );

    let deepLinkedScene: Nullable<Scene> = null;
    let newSceneForOtherDeeplink: Nullable<Scene> = null;
    let firstMyScene: Nullable<Scene> = null;
    let newSceneForProvidedGraph: Nullable<Scene> = null;
    let defaultperspectiveScene: Nullable<Scene> = null;

    if (deepLinkedSceneId) {
      deepLinkedScene = updatedScenes.find(({ id }) => id === deepLinkedSceneId) ?? null;
    }

    if (startWithNewScene) {
      newSceneForOtherDeeplink = applyNewEmptyScene(updatedScenes);
    }

    if (isProvidedGraphAvailable) {
      if (!hasPlugin) {
        if (updatedScenes.length === 0) {
          defaultperspectiveScene = applyNewEmptyScene(updatedScenes);
        }
        providedGraph = updateProvidedGraphWithId(providedGraph, defaultperspectiveScene ?? updatedScenes[0]!);
      } else {
        newSceneForProvidedGraph = temporaryScene ? temporaryScene : applyNewEmptyScene(updatedScenes);
        providedGraph = updateProvidedGraphWithId(providedGraph, newSceneForProvidedGraph);
      }
      dispatch(setStartWithProvidedGraph(null));
    }

    if (!orderedMyScenes[0] && !newSceneForOtherDeeplink && !newSceneForProvidedGraph && !defaultperspectiveScene) {
      firstMyScene = applyNewEmptyScene(updatedScenes);
    }

    const selectedScene =
      defaultperspectiveScene ??
      newSceneForProvidedGraph ??
      deepLinkedScene ??
      newSceneForOtherDeeplink ??
      lastVisitedScene ??
      orderedMyScenes[0] ??
      firstMyScene ??
      null;

    if (selectedScene) {
      const visibleRelationshipTypes = getVisibleRelationshipTypes(state);
      const visibleLabels = getVisibleLabels(state);
      const layoutType = getLayoutType(state);
      const enableCytoscape = getEnableCytoscape(state);
      const isV5OrGreater = isVersion5OorGreater(getServerVersion(state)) ?? true;
      const applySelectedSceneFn = applySelectedScene({
        dispatch,
        perspective,
        perspectiveStyle,
        visibleRelationshipTypes,
        visibleLabels,
        layoutType,
        userId,
        enableCytoscape,
        scenes: isProvidedGraphAvailable ? addingSelectedSceneIfNotExsist(updatedScenes, selectedScene) : updatedScenes,
        isV5OrGreater,
      });

      await applySelectedSceneFn(selectedScene.id, false, !!deepLinkedScene, providedGraph);
    }
  },
);

const addingSelectedSceneIfNotExsist = (allScenes: Scene[], newScene: Scene | null) => {
  if (newScene && !allScenes.some((scene) => scene.id === newScene?.id)) {
    allScenes.push(newScene);
  }
  return allScenes;
};

const updateProvidedGraphWithId = (graph: Nullable<SceneState['startWithProvidedGraph']>, scene: Nullable<Scene>) => {
  return {
    ...graph,
    id: scene?.id,
    nodes: graph?.nodes ?? [],
    relationships: graph?.relationships ?? [],
  };
};

const fetchAllScenes = async (userId: string, perspective: Perspective, userRoles: string[], scenes: Scene[]) => {
  try {
    const res = (await getAllScenes(perspective.id, userId, userRoles)) ?? [];

    const temporaryScenes = scenes.filter(
      (s) =>
        s.temporaryData !== undefined && s.temporaryData?.perspectiveId === perspective.id && s.createdBy === userId,
    );
    const loadedAndTempScenes = [...res, ...temporaryScenes];

    return loadedAndTempScenes;
  } catch (err) {
    log.error(err);
  }
};
